import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { CloudinaryContext, Image, Transformation } from 'cloudinary-react';

const PublicProfile = () => {
  const { username } = useParams();
  const [profileImage, setProfileImage] = useState('');
  const [reviews, setReviews] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [visitedParks, setVisitedParks] = useState([]);
  const [parkImages, setParkImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`https://new-npdb.onrender.com/api/user/id/${username}`);
        setUserId(response.data.userId);
        setProfileImage(response.data.profileImage);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [username]);

  useEffect(() => {
    if (!userId) return;

    const fetchReviews = async () => {
      try {
        const response = await axios.get(`https://new-npdb.onrender.com/api/reviews/user/${userId}`);
        setReviews(response.data.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setError('Failed to load reviews');
      }
    };

    fetchReviews();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    const fetchWishlist = async () => {
      try {
        const response = await axios.get(`https://new-npdb.onrender.com/api/wishlist/${userId}`);
        setWishlist(response.data);
      } catch (error) {
        console.error('Error fetching wishlist:', error);
        setError('Failed to load wishlist');
      }
    };

    const fetchVisitedParks = async () => {
      try {
        const response = await axios.get(`https://new-npdb.onrender.com/api/visited/${userId}`);
        setVisitedParks(response.data);
      } catch (error) {
        console.error('Error fetching visited parks:', error);
        setError('Failed to load visited parks');
      }
    };

    fetchWishlist();
    fetchVisitedParks();
  }, [userId]);

  useEffect(() => {
    const allParkCodes = [
      ...wishlist.map(item => item.parkCode),
      ...visitedParks.map(park => park.parkCode)
    ];

    if (allParkCodes.length === 0) return;

    const fetchParkImages = async (parkCodes) => {
      try {
        const parkImagesData = {};
        const responses = await Promise.all(
          parkCodes.map(parkCode => axios.get(`https://new-npdb.onrender.com/api/parks/code/${parkCode}`))
        );
        responses.forEach(response => {
          const data = response.data;
          if (data && data.images.length > 0) {
            parkImagesData[data.parkCode] = data.images[0]?.url;
          }
        });
        setParkImages(parkImagesData);
      } catch (error) {
        console.error('Error fetching park images:', error);
        setError('Failed to load park images');
      }
    };

    fetchParkImages(allParkCodes);
  }, [wishlist, visitedParks]);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <span key={index} className={`star ${index < rating ? 'filled' : ''}`}>
        ★
      </span>
    ));
  };

  if (loading) return <div>Loading profile...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="page-container">
      <div className="small-spacer"></div>
      <h1>{username}'s Profile</h1>
      <CloudinaryContext cloudName="dxrummmws">
        <Image publicId={profileImage} className="profile-image">
          <Transformation width="80" crop="scale" />
        </Image>
      </CloudinaryContext>

      <h2>Reviews</h2>
      {reviews.length > 0 ? (
        <div className="reviews-wrapper">
          {reviews.map((review) => (
            <div key={review._id} className="review-card">
              <h5><Link to={`/park/${review.parkCode}`} className="link">{review.fullName}</Link></h5>
              <h3 className="review-title">{review.reviewTitle}</h3>
              <div className="review-stars">{renderStars(review.rating)}</div>
              <p className="review-text">{review.review}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="off-the-wall2">No Reviews written yet.</div>
      )}

      <div className="small-spacer"></div>
      <h2>Wishlist</h2>
      <p className="table-sentence">Click on Park Name to see details.</p>
      {wishlist.length > 0 ? (
        <div className="container shadow table-responsive font-link pt-2">
          <div className="desktop-table d-none d-md-block">
            <table className="table table-sm table-dark table-bordered">
              <thead className="table-group-divider">
                <tr className="new-color">
                  <th></th>
                  <th>Park Name</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody className="new-color">
                {wishlist.map((item) => (
                  <tr key={item._id}>
                    <td>{parkImages[item.parkCode] ? (<img src={parkImages[item.parkCode]} alt={item.fullName} style={{ height: '100px', width: 'auto' }} />) : (<div>No Image</div>)}</td>
                    <td><Link to={`/park/${item.parkCode}`} className="link-dark" style={{ textDecoration: 'none' }}>{item.fullName}</Link></td>
                    <td>{item.states || 'Unknown Location'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mobile-cards d-md-none">
            {wishlist.map((item) => (
              <div className="card mb-3 shadow" key={item._id}>
                {parkImages[item.parkCode] && (<img src={parkImages[item.parkCode]} alt={item.fullName} className="img-fluid" />)}
                <div className="card-body">
                  <h5 className="card-title"><Link to={`/park/${item.parkCode}`} className="link stretched-link" style={{ textDecoration: 'none' }}>{item.fullName}</Link></h5>
                  <p className="card-text">{item.states || 'Unknown Location'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="off-the-wall2">No parks added to Wishlist yet.</div>
      )}

      <div className="small-spacer"></div>
      <h2>Visited Parks</h2>
      {visitedParks.length > 0 ? (
        <div className="container shadow table-responsive font-link pt-2">
          <div className="desktop-table d-none d-md-block">
            <table className="table table-sm table-dark table-bordered">
              <thead className="table-group-divider">
                <tr className="new-color">
                  <th></th>
                  <th>Park Name</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody className="new-color">
                {visitedParks.map((park) => (
                  <tr key={park._id}>
                    <td>{parkImages[park.parkCode] ? (<img src={parkImages[park.parkCode]} alt={park.fullName} style={{ height: '100px', width: 'auto' }} />) : (<div>No Image</div>)}</td>
                    <td><Link to={`/park/${park.parkCode}`} className="link-dark" style={{ textDecoration: 'none' }}>{park.fullName}</Link></td>
                    <td>{park.states || 'Unknown Location'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mobile-cards d-md-none">
            {visitedParks.map((park) => (
              <div className="card mb-3 shadow" key={park._id}>
                {parkImages[park.parkCode] && (<img src={parkImages[park.parkCode]} alt={park.fullName} className="img-fluid" />)}
                <div className="card-body">
                  <h5 className="card-title"><Link to={`/park/${park.parkCode}`} className="link stretched-link" style={{ textDecoration: 'none' }}>{park.fullName}</Link></h5>
                  <p className="card-text">{park.states || 'Unknown Location'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="off-the-wall2">No parks visited yet.</div>
      )}

      <div className="small-spacer"></div>
    </div>
  );
};

export default PublicProfile;
